/* eslint-disable react-refresh/only-export-components */
import { DimeChip, DimeSeparator } from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/chip')({
  component: ChipDocs,
});

type Variant =
  | 'default'
  | 'secondary'
  | 'outline'
  | 'success'
  | 'warning'
  | 'destructive'
  | 'increase'
  | 'decrease';

const VARIANTS: { value: Variant; label: string; description: string }[] = [
  { value: 'default', label: 'Default', description: 'General-purpose chip using primary color.' },
  { value: 'secondary', label: 'Secondary', description: 'Muted, lower-emphasis label.' },
  { value: 'outline', label: 'Outline', description: 'Bordered, neutral chip.' },
  {
    value: 'success',
    label: 'Success',
    description: 'Positive status like "Active" or "Approved".',
  },
  { value: 'warning', label: 'Warning', description: 'Caution status like "Pending" or "Review".' },
  { value: 'destructive', label: 'Destructive', description: 'Error or "Rejected" status.' },
  {
    value: 'increase',
    label: '+12',
    description: 'A signed figure that went up, such as a stock gain — not a success state.',
  },
  {
    value: 'decrease',
    label: '−8',
    description: 'A signed figure that went down, such as a stock loss — not a failure.',
  },
];

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-background p-6">
      {children}
    </div>
  );
}

function ChipDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Chip</h1>
      <p className="mb-8 text-muted-foreground">
        Compact status labels and tags. Use to communicate state or category at a glance.
      </p>

      <DocsSection title="Variants">
        <Preview>
          {VARIANTS.map((variant) => (
            <DimeChip key={variant.value} variant={variant.value}>
              {variant.label}
            </DimeChip>
          ))}
        </Preview>
        <table className="mt-4 w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2 pr-4 font-medium">Variant</th>
              <th className="py-2 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {VARIANTS.map((variant) => (
              <tr key={variant.value} className="border-b border-border">
                <td className="py-2 pr-4 font-mono text-xs">{variant.value}</td>
                <td className="py-2 text-muted-foreground">{variant.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DocsSection>

      <DocsSection title="With icons">
        <Preview>
          <DimeChip variant="success">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            Icon before
          </DimeChip>
          <DimeChip variant="warning">
            Icon after
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </DimeChip>
          <DimeChip variant="destructive">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            Both sides
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </DimeChip>
        </Preview>
      </DocsSection>
    </div>
  );
}
